/**
 * The Pinned column's pins (specs/formal/living-pins): newest first, four shown, and a pin whose
 * date has passed leaves by the page's own clock, not only when the server stops sending it.
 */
import type { WatchPin } from "../../../shared/types";

/** How many pins the column shows before "N older". */
export const PIN_SHOW = 4;

/** The pins still on the page at this moment (seconds), newest first. */
export function livePins(pins: readonly WatchPin[], nowSec: number): WatchPin[] {
  return pins
    .filter((p) => p.until_at == null || p.until_at > nowSec)
    .toSorted((a, b) => b.created_at - a.created_at || b.id.localeCompare(a.id));
}

/** The pins the column shows, and how many older ones sit behind "N older". */
export function shownPins(live: readonly WatchPin[], open: boolean): { shown: WatchPin[]; older: number } {
  if (open || live.length <= PIN_SHOW) return { shown: [...live], older: 0 };
  return { shown: live.slice(0, PIN_SHOW), older: live.length - PIN_SHOW };
}

/** Milliseconds until the next pin's date passes, so the page draws again then; null when none has one. */
export function msToNextExpiry(pins: readonly WatchPin[], nowSec: number): number | null {
  const next = pins.reduce<number | null>(
    (soonest, p) => (p.until_at != null && p.until_at > nowSec && (soonest == null || p.until_at < soonest) ? p.until_at : soonest),
    null,
  );
  return next == null ? null : (next - nowSec) * 1000;
}

/** Whether a pin draws the dot that says it is new: only the server's seen counts, on every device. */
export const isNewPin = (p: WatchPin): boolean => !p.seen;
